import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { stripHtml } from '../Helpers';
import useWindowLocationHref from '../Hooks/useWindowLocation';
import PrintIframe from '../PrintIframe';

const PrintSidebar = (props) => {
  const { Translations } = props;
  const href = useWindowLocationHref();

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState([]);
  const [selectedRadio, setSelectedRadio] = useState(0);
  const [printableItems, setPrintableItems] = useState([]);
  const [printableChildren, setPrintableChildren] = useState([]);
  const [printClicked, setPrintClicked] = useState(false);

  const sidebarRef = useRef(null);
  const sidebarToggleButtonRef = useRef(null);
  const elementsRef = useRef([]);

  const handleClickOutside = (event) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target) &&
      sidebarToggleButtonRef.current &&
      !sidebarToggleButtonRef.current.contains(event.target)
    ) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    const mainElement = document.querySelector('main');
    const elements = mainElement
      ? mainElement.querySelectorAll('[data-print-sidebar-title]')
      : [];
    const heading = mainElement ? mainElement.querySelector('h1') : '';
    elementsRef.current = elements ?? [];

    const imgblockMediaSliderChildren = Array.from(elements)
      .filter((item) => item.nodeName === 'FIGURE')
      .filter((figures) =>
        figures.className.includes('c-imageblock--is-gallery')
      );

    const chapterContainerChildren = Array.from(elements).filter(
      (item) =>
        item.getAttribute('data-is-member-of-chapter-container') === 'true'
    );

    const chapterChildren = Array.from(elements).filter(
      (item) => item.getAttribute('data-is-member-of-chapter') === 'true'
    );

    const links = Array.from(elements).filter(
      (item) =>
        item.dataset.linklist === 'link-list-container' &&
        item.offsetParent &&
        item.offsetParent.hasAttribute('data-list-container')
    );

    setPrintableChildren([
      ...imgblockMediaSliderChildren,
      ...links,
      ...chapterContainerChildren,
      ...chapterChildren,
    ]);

    const removedContainerChildren = Array.from(elements).filter(
      (item) =>
        item.offsetParent &&
        !item.offsetParent.hasAttribute('data-list-container') &&
        !item.className.includes('c-imageblock--is-gallery') &&
        item.getAttribute('data-is-member-of-chapter-container') !== 'true' &&
        item.getAttribute('data-is-member-of-chapter') !== 'true'
    );

    setPrintableItems(removedContainerChildren);

    if (elements?.item(0) === heading) {
      handlePrintSelection(heading);
    }
  }, []);

  useEffect(() => {
    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen]);

  const handleRadioChange = (index) => {
    setSelectedRadio(index);
  };

  const handlePrintSelection = (item) => {
    const mediaType = item.getAttribute('data-type-media-slider');
    const linkListContainer = item.getAttribute('data-list-container');
    const chapterContainer = item.getAttribute('data-chapter-container');
    const chapter = item.getAttribute('data-is-chapter');

    let listOfImgElement = [];
    const isMediaSlider = mediaType === 'MediaSlider';
    const isLinkListContainer = linkListContainer === 'data-list-container';
    const isChapterContainer = chapterContainer === 'data-chapter-container';
    const isChapter = chapter === 'isChapter';

    if (isMediaSlider) {
      listOfImgElement = printableChildren
        ?.filter((item) => item.nodeName === 'FIGURE')
        .filter((figures) =>
          figures.className.includes('c-imageblock--is-gallery')
        );
    }
    if (isChapter) {
      const listOfElementblockchapter = printableChildren?.filter(
        (name) => name.getAttribute('data-is-member-of-chapter') === 'true'
      );
      listOfImgElement = [...listOfImgElement, ...listOfElementblockchapter];
    }
    if (isChapterContainer) {
      const listOfElementblock = printableChildren?.filter(
        (name) =>
          name.getAttribute('data-is-member-of-chapter-container') === 'true'
      );
      listOfImgElement = [...listOfImgElement, ...listOfElementblock];
    }
    if (isLinkListContainer) {
      const listOfElement = printableChildren?.filter((item) =>
        item.offsetParent.hasAttribute('data-list-container')
      );
      listOfImgElement = [...listOfImgElement, ...listOfElement];
    }

    setCheckedItems((prevItems) => {
      if (!prevItems.includes(item)) {
        const newItemsList = [...prevItems, item, ...listOfImgElement];
        return [...new Set(newItemsList)];
      }

      const filteredItems = prevItems.filter((i) => i !== item);

      if (isMediaSlider) {
        return filteredItems.filter(
          (figures) => !figures.className.includes('c-imageblock--is-gallery')
        );
      }

      if (isLinkListContainer) {
        return filteredItems.filter(
          (item) => !item.offsetParent?.hasAttribute('data-list-container')
        );
      }
      if (isChapterContainer) {
        return filteredItems.filter(
          (item) =>
            item.getAttribute('data-is-member-of-chapter-container') !== 'true'
        );
      }
      if (isChapter) {
        return filteredItems.filter(
          (item) => item.getAttribute('data-is-member-of-chapter') !== 'true'
        );
      }
      return filteredItems;
    });
  };

  const togglePrintSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const getPrintSelectionTitle = (item) => {
    let dataPrintSidebarTitle = item.getAttribute('data-print-sidebar-title');
    let dataPrintSidebarTitleRegion = item.getAttribute(
      'data-print-sidebar-title-region'
    );

    if (dataPrintSidebarTitle) {
      const words = dataPrintSidebarTitle.split(/\s+/);
      return words.length > 5
        ? words.slice(0, 5).join(' ') +
            '...' +
            (dataPrintSidebarTitleRegion ? ` (Regionalt)` : '')
        : dataPrintSidebarTitle +
            (dataPrintSidebarTitleRegion ? ` (Regionalt)` : '');
    } else {
      const words = item.innerText.split(/\s+/);
      return words.length > 5
        ? words.slice(0, 5).join(' ') + '...'
        : item.innerText;
    }
  };

  return (
    <>
      <button
        ref={sidebarToggleButtonRef}
        role="button"
        aria-label={Translations && Translations.print}
        onClick={(e) => {
          e.preventDefault();
          togglePrintSidebar();
        }}
        title={Translations && Translations.print}
        className="c-printsidebar-toggle-button"
        data-event-category={href || 'novalue'}
        data-event-action={Translations.print}
        data-event-value={href || 'novalue'}
        data-event-name={Translations.print}
      >
        <span
          data-event-include
          className="c-share__icon c-share__icon--print"
        ></span>
        <span className="show-for-sr">
          {Translations && Translations.print}
        </span>
      </button>
      <div
        ref={sidebarRef}
        className={classNames('c-printsidebar', {
          'c-printsidebar__is-open': isSidebarOpen,
        })}
        aria-hidden={!isSidebarOpen}
      >
        <div className="c-printsidebar__header">
          <h1 className="c-printsidebar__header__heading">
            {Translations.printsidebartitle}
          </h1>
          <button
            className="c-printsidebar__header__close-button"
            data-event-category={href || 'novalue'}
            data-event-action={Translations.print}
            data-event-value={href || 'novalue'}
            data-event-name={Translations.print}
            onClick={togglePrintSidebar}
          >
            {Translations.printsidebarclose}
            <i className="c-printsidebar__header__close-button__icon--close" />
          </button>
        </div>
        <div className="c-printsidebar__subheader">
          <h2 className="c-printsidebar__subheading">
            {Translations.printsidebarselectiontitle}
          </h2>
          <div className="c-printsidebar__radio-container">
            {Translations.printsidebarselectionparts && (
              <div
                aria-label={Translations.printsidebarselectionparts}
                onClick={() => handleRadioChange(0)}
              >
                <button
                  type="radio"
                  className={classNames({ selected: selectedRadio === 0 })}
                />
                <span>{Translations.printsidebarselectionparts}</span>
              </div>
            )}
            {Translations.printsidebarselectionwholepage && (
              <div
                aria-label={Translations.printsidebarselectionwholepage}
                onClick={() => handleRadioChange(1)}
              >
                <button
                  type="radio"
                  className={classNames({ selected: selectedRadio === 1 })}
                />
                <span>{Translations.printsidebarselectionwholepage}</span>
              </div>
            )}
          </div>
        </div>
        {selectedRadio !== 1 && (
          <div className="c-printsidebar__selection-container">
            <p>{Translations.printsidebarselectionwhatparts}</p>
            <div className="c-printsidebar__selection-container__checkbox-container">
              {printableItems &&
                printableItems.map((item, index) => {
                  if (!item?.innerText) return;
                  let content = (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: stripHtml(getPrintSelectionTitle(item)),
                      }}
                    />
                  );
                  return (
                    <div
                      key={index}
                      aria-label={Translations.printsidebarselectionparts}
                      onClick={() => handlePrintSelection(item)}
                    >
                      <button
                        type="checkbox"
                        id={`print-checkbox-${index}`}
                        className={classNames({
                          checked: checkedItems.includes(item),
                        })}
                      />
                      {content}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        <div className="c-printsidebar__button-container">
          {selectedRadio === 1 ? (
            <button
              data-event-category={href || 'novalue'}
              data-event-action={Translations.printbuttonwholepage}
              data-event-value={href || 'novalue'}
              data-event-name={Translations.printbuttonwholepage}
              className="c-printsidebar__button"
              type="button"
              onClick={() => setPrintClicked(true)}
            >
              <i className="c-printsidebar__button__icon--print" />
              {Translations.printbuttonwholepage}
            </button>
          ) : (
            <button
              data-event-category={href || 'novalue'}
              data-event-action={Translations.printbuttonparts}
              data-event-value={href || 'novalue'}
              data-event-name={Translations.printbuttonparts}
              className="c-printsidebar__button"
              type="button"
              onClick={() => setPrintClicked(true)}
            >
              <i className="c-printsidebar__button__icon--print" />
              {Translations.printbuttonparts}
            </button>
          )}
        </div>
      </div>
      {printClicked && (
        <PrintIframe
          SelectedRadio={selectedRadio}
          PrintableItems={printableItems}
          CheckedItems={checkedItems}
          SetPrintClicked={setPrintClicked}
        />
      )}
    </>
  );
};

export default PrintSidebar;
